"use client";

import { useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Container } from "@/components/ui/Container";
import { TrustStars } from "@/components/TrustStars";
import { GoogleStars } from "@/components/GoogleStars";
import { GoogleLogo } from "@/components/GoogleLogo";
import { useExperimentVariant } from "@/hooks/useExperimentVariant";
import { site } from "@/lib/site";
import { cn } from "@/lib/utils";

type Review = { name: string; date: string; text: string };

// card width (280) + gap (20px = gap-5)
const STEP = 300;

// Laurel branch (from AirHelp's leaves.svg), inlined so it always renders.
// Colour comes from `text-*` in className (defaults to white for the blue card).
function Leaves({ flip = false, className }: { flip?: boolean; className?: string }) {
  return (
    <svg
      viewBox="0 0 23 58"
      fill="currentColor"
      aria-hidden="true"
      className={cn("h-10 w-auto text-white", flip && "-scale-x-100", className)}
    >
      <path d="M22.76 57.33c-.18-.16-17.78-17.01-10.3-44.14a.35.35 0 0 0-.24-.44c-.3-.08-.6.1-.69.4C4.04 40.7 21.95 57.83 22.13 58l.63-.67Z" />
      <path d="M3.48 41.73c2.4 7.49 9.44 5.7 9.44 5.7-2-7.34-9.44-5.7-9.44-5.7ZM9.79 38.6C.86 39.58.7 31.42.7 31.42c8.57-.16 9.08 7.18 9.08 7.18ZM.67 19.89c.93 10.3 7.98 9.2 7.98 9.2.58-9.6-7.98-9.2-7.98-9.2ZM5.4 5.13c-4.67 9.9 3.28 13.39 3.28 13.39C12.32 9.8 5.4 5.13 5.4 5.13ZM15.45 0c-7.7 4.15-3.13 11.82-3.13 11.82C20.58 4.72 15.45 0 15.45 0ZM22.8 37.01c-8.54 1.94-7.01 9.33-7.01 9.33C24.85 44.63 22.8 37 22.8 37ZM20.84 27.24c-8.26 2.46-7.7 10.1-7.7 10.1 8.21.21 7.7-10.1 7.7-10.1ZM20.48 19.24c-8.61.36-8.4 9.18-8.4 9.18 5.53.92 8.4-9.18 8.4-9.18ZM20.48 10.26c-7.84 1.28-7.59 8.16-7.59 8.16 7.5-.5 7.6-8.16 7.6-8.16Z" />
    </svg>
  );
}

function ReviewCard({ review, google = false }: { review: Review; google?: boolean }) {
  return (
    <figure className="flex w-[280px] shrink-0 snap-start flex-col rounded-xl bg-mist p-5">
      {google ? <GoogleStars size={20} /> : <TrustStars size={20} />}
      <blockquote className="mt-4 flex-1 text-[15px] font-medium leading-snug text-navy">
        {review.text}
      </blockquote>
      <figcaption className="mt-4">
        <p className="text-xs font-bold uppercase tracking-wide text-navy">{review.name}</p>
        <p className="mt-0.5 text-xs text-muted">{review.date}</p>
      </figcaption>
    </figure>
  );
}

function GoogleRatingCard({ className }: { className?: string }) {
  const t = useTranslations("SocialProof");
  return (
    <div
      className={cn(
        "flex-col items-center justify-center rounded-xl border border-line bg-white p-6 text-center",
        className,
      )}
    >
      <div className="flex items-center gap-2">
        <GoogleLogo className="h-6 w-6" />
        <span className="text-sm font-semibold text-navy">{t("googleLabel")}</span>
      </div>
      <div className="mt-3 flex items-center justify-center gap-3">
        <Leaves className="text-navy" />
        <p className="text-4xl font-extrabold text-navy">{site.rating.value.toString()}/5</p>
        <Leaves flip className="text-navy" />
      </div>
      <div className="mt-2">
        <GoogleStars size={22} />
      </div>
      <p className="mt-4 text-xs leading-relaxed text-muted">
        {t("ratingCardGoogle", { count: site.rating.count })}
      </p>
      <a
        href={site.reviewsUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-4 text-xs font-semibold text-[#1a73e8] underline decoration-[#1a73e8]/30 underline-offset-4 transition hover:decoration-[#1a73e8]"
      >
        {t("seeAll")}
      </a>
    </div>
  );
}

function RatingCard({ className }: { className?: string }) {
  const t = useTranslations("SocialProof");
  return (
    <div
      className={cn(
        "relative flex-col items-center justify-center overflow-hidden rounded-xl bg-gradient-to-br from-bright via-brand to-brand-dark p-6 text-white",
        className,
      )}
    >
      <div className="pointer-events-none absolute -right-12 -top-12 size-36 rounded-full bg-white/15 blur-2xl" />
      <div className="relative flex items-center gap-3">
        <Leaves />
        <p className="text-4xl font-extrabold text-white">{site.rating.value.toString()}/5</p>
        <Leaves flip />
      </div>
      <div className="relative mt-3">
        <TrustStars size={22} />
      </div>
      <p className="relative mt-4 text-center text-xs leading-relaxed text-white/75">
        {t("ratingCard", {
          count: site.rating.count,
        })}
      </p>
      {/* "See all reviews" link hidden for now — restore when the reviews page is ready.
      <a
        href={site.reviewsUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="relative mt-4 text-xs font-semibold text-white underline decoration-white/40 underline-offset-4 transition hover:decoration-white"
      >
        {t("seeAll")}
      </a>
      */}
    </div>
  );
}

export function TrustStrip() {
  const t = useTranslations("SocialProof");
  const tr = useTranslations("Reviews");
  const items = tr.raw("items") as Review[];

  // `reviews-google-snippet` presence experiment: the Google-reviews snippet
  // (Google logo + gold stars) is the control/SEO-default; the "green" holdback
  // variant swaps back to the old Trustpilot-look card after the flag loads.
  const green = useExperimentVariant("reviews-google-snippet") === "green";
  const Rating = green ? RatingCard : GoogleRatingCard;

  const scrollRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);

  const onScroll = () => {
    const el = scrollRef.current;
    if (el) setActive(Math.round(el.scrollLeft / STEP));
  };
  const move = (dir: number) => {
    scrollRef.current?.scrollBy({ left: dir * STEP, behavior: "smooth" });
  };
  const goTo = (i: number) => {
    scrollRef.current?.scrollTo({ left: i * STEP, behavior: "smooth" });
  };

  return (
    <section className="bg-white py-10 lg:py-12" data-track-section="social_proof">
      <Container>
        <h2 className="text-center text-xl font-extrabold tracking-tight text-navy sm:text-2xl">
          {t.rich("title", {
            count: site.helped,
            b: (chunks) => <span className="text-brand">{chunks}</span>,
          })}
        </h2>
      </Container>

      <Container className="mt-8">
        <div className="flex gap-4 lg:gap-5">
          {/* Rating card - fixed sidebar on desktop */}
          <Rating className="hidden shrink-0 sm:flex sm:w-[260px]" />

          {/* Scrollable review carousel. On mobile it bleeds past the container's
              right padding (-mr-5) so the next review peeks off the screen edge,
              signalling that the row is swipeable. */}
          <div
            ref={scrollRef}
            onScroll={onScroll}
            className="no-scrollbar -mr-5 flex flex-1 snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth sm:mr-0 lg:gap-5"
          >
            {/* On mobile the rating card rides along as the first carousel item */}
            <Rating className="flex w-[280px] shrink-0 snap-start sm:hidden" />
            {items.map((review, i) => (
              <ReviewCard key={`${review.name}-${i}`} review={review} google={!green} />
            ))}
          </div>
        </div>

        {/* Controls */}
        <div className="mt-7 flex items-center justify-center gap-4">
          <button
            type="button"
            onClick={() => move(-1)}
            aria-label="Vorige"
            className="grid size-9 place-items-center rounded-full border border-line text-navy transition hover:bg-mist"
          >
            <ChevronLeft className="size-5" />
          </button>

          <div className="flex items-center">
            {items.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => goTo(i)}
                aria-label={`Review ${i + 1}`}
                aria-current={i === active}
                className="grid size-6 place-items-center"
              >
                <span
                  className={cn(
                    "h-2 rounded-full transition-all",
                    i === active ? "w-5 bg-brand" : "w-2 bg-line",
                  )}
                />
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={() => move(1)}
            aria-label="Volgende"
            className="grid size-9 place-items-center rounded-full border border-line text-navy transition hover:bg-mist"
          >
            <ChevronRight className="size-5" />
          </button>
        </div>
      </Container>
    </section>
  );
}
